/* ============================================================
   plans.js — source unique des plans Revyo (front)
   ------------------------------------------------------------
   Métadonnées non-traduisibles (id, emoji, price, accent, popular).
   Les labels/tagline/features sont lus via i18n (namespace `tabs`).

   ⚠️ Les `id` doivent matcher ceux acceptés par
   POST /api/stripe/create-checkout-session côté backend
   (decouverte | pro | intensif, + essentiel legacy). Source de vérité
   Stripe Price ID : variables d'env Railway STRIPE_PRICE_DECOUVERTE /
   _ESSENTIEL / _PRO / _INTENSIF.

   ⚠️ PLAN_IDS = plans AFFICHÉS dans la grille publique.
   'essentiel' (69 €) en est volontairement ABSENT depuis le 30/07/2026 :
   il n'est plus vendu, mais reste dans PLAN_META (legacy: true) pour que
   TabAbonnement affiche correctement « Votre plan actuel » à un abonné
   d'avant cette date. Ne jamais retirer l'entrée `essentiel` de PLAN_META.

   ⚠️ Les prix ne se rendent PAS en `{price}€` : utiliser formatPrice()
   de utils/format.js (9,99 € en fr/es/it, €9.99 en en).

   Source du pricing : CLAUDE.md (validé 30 juillet 2026).
   ============================================================ */

/** Plans affichés dans la grille publique (ordre d'affichage). */
export const PLAN_IDS = ['decouverte', 'pro', 'intensif'];

export const PLAN_META = {
  decouverte: { id: 'decouverte', emoji: '🌱', price: 9.99, accent: false, popular: false },
  pro:        { id: 'pro',        emoji: '🚀', price: 89,   accent: true,  popular: true  },
  intensif:   { id: 'intensif',   emoji: '👑', price: 179,  accent: false, popular: false },
  // --- LEGACY — hors grille publique, conservé pour les abonnés existants ---
  essentiel:  { id: 'essentiel',  emoji: '🌿', price: 69,   accent: false, popular: false, legacy: true },
};

/** Lookup rapide par id (rétrocompat) : PLAN_BY_ID['pro'].price → 89.
 *  Utilisé par les composants qui ne lisent que les métadonnées (price, emoji).
 *  Pour avoir aussi label/tagline/features traduits, utiliser buildPlans(t)
 *  ou getPlanById(id, t). */
export const PLAN_BY_ID = PLAN_META;

/* ------------------------------------------------------------------
   ALIAS HISTORIQUES de users.plan — NE PAS SUPPRIMER
   ------------------------------------------------------------------
   Vérifié en base le 30/07/2026 : les comptes réels ne portent PAS
   'essentiel' mais 'starter' (alias historique du palier bas, déjà mappé
   vers « Essentiel » dans dashboard-static.html). 'premium' est l'alias
   du palier haut.

   Sans cette table, TabAbonnement afficherait « Aucun abonnement actif »
   à un abonné payant — PLAN_META[id] serait undefined.

   'gratuit' n'est volontairement PAS aliasé : ce plan est posé par le
   webhook Stripe à la résiliation, en même temps que account_status =
   'paused'. L'empty state « Aucun abonnement actif » est alors le bon
   affichage (comportement actuel conservé).
   ------------------------------------------------------------------ */
export const PLAN_ALIASES = {
  starter: 'essentiel',
  premium: 'intensif',
};

/** Résout un alias historique vers son plan canonique. */
export function resolvePlanId(id) {
  if (!id) return id;
  return PLAN_ALIASES[id] || id;
}

/** true si le plan existe mais n'est plus commercialisé (Essentiel 69 €). */
export function isLegacyPlan(id) {
  return Boolean(PLAN_META[resolvePlanId(id)]?.legacy);
}

/**
 * Métadonnées + libellés traduits d'UN plan, y compris les plans legacy.
 *
 * Indispensable au bandeau « Votre plan actuel » de TabAbonnement : un abonné
 * Essentiel n'apparaît dans aucune carte de la grille (PLAN_IDS ne le contient
 * pas), il faut donc pouvoir résoudre son label hors de buildPlans().
 *
 * Retourne null si l'id est inconnu (compte sans plan → empty state).
 */
export function getPlanById(id, t) {
  // Les alias historiques (starter → essentiel, premium → intensif) sont résolus
  // ici : mêmes métadonnées et mêmes libellés i18n que leur plan canonique.
  const canonicalId = resolvePlanId(id);
  const meta = PLAN_META[canonicalId];
  if (!meta) return null;
  if (typeof t !== 'function') return { ...meta };
  return {
    ...meta,
    label: t(`abonnement.plans.${canonicalId}.label`),
    tagline: t(`abonnement.plans.${canonicalId}.tagline`),
    features: t(`abonnement.plans.${canonicalId}.features`, { returnObjects: true }) || [],
  };
}

/**
 * Liste enrichie i18n des plans VENDUS. À appeler dans le composant qui a `t`.
 *
 * Usage :
 *   const { t } = useTranslation('tabs');
 *   const plans = buildPlans(t);
 *   plans[0].label  → "Découverte" en FR, "Discovery" en EN, etc.
 */
export function buildPlans(t) {
  return PLAN_IDS.map(id => getPlanById(id, t));
}
